import cloneDeep from 'lodash/cloneDeep'
import {generateUUID} from './uuid'
import {
  CONTRACT_COMPLETIONS,
  CONTRACT_TYPE_REGISTRATION,
  CONTRACT_TYPE_SILVICULTURE_AGREEMENT,
  CONTRACT_TYPE_SILVICULTURE_ORDER,
  CONTRACT_TYPE_SUPPLY_WOOD,
  CONTRACT_TYPES,
  DEFAULT_FORESTFUND_RATE, ENTITY_TYPE_CONTRACT, ENTITY_TYPE_QUOTE, MAX_FORESTFUND_RATE, MIN_FORESTFUND_RATE
} from '~/constants/contracts'
import {CUSTOMER_SELF_CONTRACTOR_ID} from '~/constants/volumeAndContractor'
import {setCalculatedYearMonthAndPeriod} from './deliveryDates'
import type {User} from '~/models/auth'
import type {Contract, FullContract} from '~/models/contract'
import type {Customer, Property} from '~/models/customers'
import type {StepDefinition} from '~/models/wizardSchema'
import {
  CONTRACT_STATES,
  CONTRACT_STATES_DRAFT,
  STATUS_SBL, STATUS_SBL_ACTIVATED, STATUS_SBL_DRAFT,
} from '~/constants/contract'
import {compareObjectsGetDifferingKeysAndValues, getCurrentYearMonth, getMonthDifference} from "~/helpers/util";
import {isStatusSblAtLeast} from '~/helpers/statusSbl'

export const getContractTypeLabel = (type?: number): string => {
  return CONTRACT_TYPES.find((s) => s.Id === type)
    ? CONTRACT_TYPES.find((s) => s.Id === type)?.Name || ''
    : ''
}

export const getHasFrameworkAgreementExpired = (frameworkAgreementExpires?: string) : boolean => {
  if (!!frameworkAgreementExpires) {
    const now = new Date()
    const expiredDate = new Date(frameworkAgreementExpires)
    return expiredDate < now
  }
  return false
}

export const getDefaultQuoteData =  (type: number, customer: Customer, property: Property, manager: User): FullContract => {
  let data = getDefaultEntityData(ENTITY_TYPE_QUOTE, type, customer, property, manager)
  data = {
    ...data,
    QuoteDetails: {
      AcceptDeadline: '',
      Name: '',
    }
  }
  return data;
}

export const getDefaultContractData = (type: number, customer: Customer, property: Property, manager: User): FullContract => {
  return getDefaultEntityData(ENTITY_TYPE_CONTRACT, type, customer, property, manager)
}

export const getDefaultEntityData = (entityType: number, type: number, customer: Customer, property: Property, manager: User): FullContract => {
  let contract: FullContract = {
    Id: 'temp_' + generateUUID(),
    Type: type,
    EntityType: entityType,
    Status: CONTRACT_STATES_DRAFT,
    StatusSbl: STATUS_SBL_DRAFT,
    Pefc: true,
    CertificationType: !property.CocCode ? 1 : 9,
    PropertyId: property.Id,
    ForestFundRate: DEFAULT_FORESTFUND_RATE,
    ForestOwnerId: customer.Id,
    OwnerId: manager?.Id,
    OwnerName: manager?.FullName,
    Year: new Date().getFullYear(),
    RoadOperationContact: customer.Name,
    RoadOperationPhone: customer.Mobile,
    EnvironmentalReportDueDilligence: true,
    AssortmentInformation: {
      EntrepreneurCapacity: 0,
      EntrepreneurId: type === CONTRACT_TYPE_SUPPLY_WOOD && !customer.IsLargeForestOwner ? CUSTOMER_SELF_CONTRACTOR_ID : null,
      TotalVolume: 0,
      TotalWoodForSale: 0,
      SumVolume: 0,
      WoodForOwnUseVolume: 0
    },
    Sivilculture: {
      ForestOwnerTransportsPlants: false
    },
    Environment: {},
    AgreementDetails: {
      OperationCostType: 1
    },
    Completition: {},
    ActionPlans: [],
    TopplePlaces: [],
    Details: [],
    MapImages: [],
    Meta: {},
    Description: '',
  }

  if ([CONTRACT_TYPE_SILVICULTURE_AGREEMENT, CONTRACT_TYPE_SILVICULTURE_ORDER].includes(type)) {
    contract = setCalculatedYearMonthAndPeriod(contract)
    contract.EnvironmentReport = {
      Environments: {},
      Planning: {}
    }
  } else if (type === CONTRACT_TYPE_REGISTRATION) {
    contract.AgreementDetails!.OperationCostType = 3 // Manual
    contract.AgreementDetails!.OperationPrice = 0
  } else if (type === CONTRACT_TYPE_SUPPLY_WOOD) {
    contract.Environment!.EnvironmentResponsibleName = customer.LScourse ? customer.Name : ''
    contract.Environment!.EnvironmentResponsibleEmail = customer.LScourse ? customer.Email : ''
    contract.EnvironmentReport = {
      Environments: {},
      Planning: {}
    }
  } else {
    contract.EnvironmentReport = {
      Environments: {},
      Planning: {}
    }
  }

  return contract
}

export const getTotalVolumeFromActions = (contract: FullContract): number => {
  const activeActionPlans = contract.ActionPlans?.filter(x => !x.Deleted) || []
  const activeAllmaJobs = contract.AllmaJobs?.filter(x => !x.Removed) || []

  if (activeAllmaJobs.length > 0) {
    return activeAllmaJobs.reduce((prev, cur) => {
      return prev + cur.Actions?.reduce((p, c) => c.TotalVolume && !c.AdditionalOrder ? p + c.TotalVolume : p, 0)
    }, 0)
  } else {
    return activeActionPlans.reduce((prev, cur) => cur.TotalVolume && !cur.AdditionalOrder ? prev + cur.TotalVolume : prev + 0, 0)
  }
}

export const getContractStateLabel = (contract?: Contract): string => CONTRACT_STATES.find(s => s.Id === contract?.Status) ? CONTRACT_STATES.find(s => s.Id === contract?.Status)?.Name || '' : ''
export const getStatusSblLabel = (contract?: Contract): string => STATUS_SBL.find(s => s.Id === contract?.StatusSbl) ? STATUS_SBL.find(s => s.Id === contract?.StatusSbl)?.Name || '' : ''

export const getContractCompletionLabel = (contract: FullContract): string =>
  CONTRACT_COMPLETIONS.find(s => s.Id === contract.AssortmentInformation!.CompleteOperationTarget) ? CONTRACT_COMPLETIONS.find(s => s.Id === contract.AssortmentInformation?.CompleteOperationTarget)?.Name : ''

export const isDraft = (contract: Contract | FullContract): boolean => !contract.Id || contract?.Id?.includes('temp_')

export const cleanDraftContract = (contract: FullContract): FullContract => {
  const tmpContract = cloneDeep(contract)
  if (tmpContract.Id?.includes('temp_')) {
    tmpContract.TempId = tmpContract.Id
    delete tmpContract.Id
  }

  tmpContract.ActionPlans?.forEach((actionPlan) => {
    if (actionPlan.Id?.includes('temp_')) {
      delete actionPlan.Id
    }
  })

  tmpContract.TopplePlaces?.forEach((topplePlace) => {
    if (topplePlace.Id?.includes('temp_')) {
      delete topplePlace.Id
    }
  })

  tmpContract.Details?.forEach((detail) => {
    if (detail.Id?.includes('temp_')) {
      delete detail.Id
    }
  })

  tmpContract.MapImages?.forEach((image) => {
    if (image.Id?.includes('temp_')) {
      delete image.Id
    }
  })

  return tmpContract
}

export const contractToFull = (contract: Contract): FullContract => ({
  Id: contract.Id,
  Name: contract.Name,
  ContractNo: contract.ContractNo,
  VsysNo: contract.VsysNo,
  Type: contract.Type,
  SignedOn: contract.SignedOn,
  CreatedDate: contract.CreatedDate,
  ModifiedDate: contract.ModifiedDate,
  Status: contract.Status,
  ForestOwnerId: contract.ForestOwnerId,
  PropertyId: contract.PropertyId,
  AssortmentInformation: {
    EntrepreneurId: contract.EntrepreneurTeamId,
    SumVolume: contract.Volume
  },
  VolumeProduced: contract.VolumeProduced,
  VolumeTimberLot: contract.VolumeTimberLot,
  VolumeDelivery: contract.VolumeDelivery,
  VolumeMeasured: contract.VolumeMeasured,
  Year: contract.Year,
  LoggingMonth: contract.LoggingMonth
})

export const contractIsExcludedFromForestFund = (contract: FullContract) => {
  return contract.ExcludedFromForestFund || contract.ForestFundRate === 0
}

export const isForestFundValid = (excludedFromForestFund: boolean, value: number) => {
    return (excludedFromForestFund || (value >= MIN_FORESTFUND_RATE && value <= MAX_FORESTFUND_RATE))
}

export const detailsMissingQuantityInContract = (contract: FullContract) => {
  return contract.Details?.filter((detail) => detail.Quantity === 0) || []
}

export const detailsMissingPriceInContract = (contract: FullContract) => {
  return contract.Details?.filter(
    (detail) => detail.PriceType === undefined || !detail.TimberPriceListId
  ) || []
}

export const hasPriceListChanges = (contract: FullContract, contractReference: FullContract) => {
  const currentPriceListIds = (contractReference.Details || []).map(d => d.TimberPriceListId)
  const newPriceListIds = (contract.Details || []).map(d => d.TimberPriceListId)
  if (currentPriceListIds.length !== newPriceListIds.length) {
    return true
  }
  return currentPriceListIds.some((id, i) => id !== newPriceListIds[i])
}

const KEYS_TO_IGNORE_REGEX: RegExp[] = [
  /^Details\.\d+\.Percent$/,
  /^PriceListAllocationCode$/,
  /^ModifiedDate$/,
  /^SendPdfEmail$/,
  /^SignatureOwner$/,
  /^SignatureBuyer$/,
  /^OwnerName$/,
  /^SblSignedOn$/,
  /^StatusSbl$/,
  /^AssortmentInformation.TotalVolume$/,
];


function filterIgnoredKeys(changes: { key: string; value1: any; value2: any }[]): { key: string; value1: any; value2: any }[] {
    return changes.filter(change => !KEYS_TO_IGNORE_REGEX.some(regex => regex.test(change.key)))
}

export const getUnsavedChanges = (contract: FullContract, contractReference: FullContract) => {
    const changes = compareObjectsGetDifferingKeysAndValues(contractReference, contract)
    const filteredChanges = filterIgnoredKeys(changes)
    if (!!filteredChanges.length) {
        console.log('Unsaved changes:', filteredChanges)
    }

    return filteredChanges
}

export const hasUnsavedChanges = (contract: FullContract, contractReference: FullContract) => {
  return !!(getUnsavedChanges(contract, contractReference).length)
}

export const getHasOutdatedPeriod = (contract: FullContract) => {
  if (isStatusSblAtLeast(contract.StatusSbl, STATUS_SBL_ACTIVATED))
    return false;
  
  const currentPeriod = getCurrentYearMonth()
  const diff = getMonthDifference('' + contract.Year + contract.LoggingMonth?.toString().padStart(2, '0'), currentPeriod)
  return diff > 0
}

export const contractTypeAllowsMultipleJobs = (contractType: number | null | undefined) => {
  return contractType !== CONTRACT_TYPE_SILVICULTURE_AGREEMENT;
}